import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, Sun, Moon, Heart, Sparkles, Activity, Shield, Clock, Video, Layers, Package, User, Eye, FileText, HelpCircle, ChevronRight, MessageCircle, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
interface PricingPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-21406">
      {children}
    </motion.section>;
}
export function PricingPage({
  onNavigate
}: PricingPageProps) {
  const servicesRef = useRef<HTMLDivElement>(null);
  const {
    addItem
  } = useCart();
  const handleBuyPackage = (pkg: {
    title: string;
    desc: string;
    price: number;
    oldPrice?: number;
  }) => {
    addItem({
      id: `pkg-${Date.now()}`,
      name: pkg.title,
      description: pkg.desc,
      price: pkg.price,
      type: 'package',
      format: 'PDF-отчёты',
      duration: 'Бессрочно',
      oldPrice: pkg.oldPrice,
      badges: [{
        label: 'Пакет отчётов',
        variant: 'success'
      }]
    });
    onNavigate('checkout');
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-21407">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 pb-14 lg:pb-16" data-id="element-21408">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-indigo-500/20 rounded-full blur-[120px] pointer-events-none" data-id="element-21409" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-violet-500/15 rounded-full blur-[100px] pointer-events-none" data-id="element-21410" />
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.8)_1px,transparent_1px)] bg-[length:40px_40px] pointer-events-none" data-id="element-21411" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-21412">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Цены'
        }]} className="mb-8 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-21413" />

          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl mx-auto text-center relative" data-id="element-21414">
            <motion.h1 variants={staggerItem} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight" data-id="element-21415">
              Выберите астрологический анализ
            </motion.h1>
            <motion.p variants={staggerItem} className="text-base sm:text-lg text-slate-300 mb-3 leading-relaxed max-w-2xl mx-auto" data-id="element-21416">
              Получите персональный астрологический разбор личности, отношений и
              жизненных периодов.
            </motion.p>
            <motion.p variants={staggerItem} className="text-sm text-slate-400 mb-7 max-w-xl mx-auto bg-white/5 py-2 px-4 rounded-full inline-block backdrop-blur-sm border border-white/10" data-id="element-21417">
              Каждая услуга включает бесплатный Beta-результат и полный
              Premium-отчёт.
            </motion.p>
            <motion.div variants={staggerItem} className="flex flex-col items-center" data-id="element-21418">
              <Button size="lg" onClick={() => onNavigate('free-chart')} className="bg-indigo-500 hover:bg-indigo-600 shadow-lg shadow-indigo-500/30 w-full sm:w-auto" data-id="element-21419">
                Построить натальную карту
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-21420" />
              </Button>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6 text-xs sm:text-sm text-slate-400" data-id="element-21421">
                <div className="flex items-center" data-id="element-21422">
                  <User className="w-3.5 h-3.5 mr-1.5 text-indigo-400" data-id="element-21423" />
                  <span data-id="element-21424">10 000+ отчётов</span>
                </div>
                <div className="flex items-center" data-id="element-21425">
                  <Zap className="w-3.5 h-3.5 mr-1.5 text-amber-400" data-id="element-21426" />
                  <span data-id="element-21427">Мгновенный результат</span>
                </div>
                <div className="flex items-center" data-id="element-21428">
                  <Shield className="w-3.5 h-3.5 mr-1.5 text-emerald-400" data-id="element-21429" />
                  <span data-id="element-21430">Безопасная оплата</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* 2. КАК РАБОТАЕТ ПЛАТФОРМА */}
      <AnimatedSection className="py-12 bg-white" data-id="element-21431">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21432">
          <div className="text-center mb-10" data-id="element-21433">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-21434">
              Как работает платформа
            </h2>
            <p className="text-sm text-slate-600" data-id="element-21435">
              Простой путь от ввода данных до получения полного персонального
              разбора
            </p>
          </div>

          <div className="relative" data-id="element-21436">
            <div className="hidden md:block absolute top-10 left-[15%] right-[15%] h-0.5 border-t-2 border-dashed border-slate-200" data-id="element-21437" />
            <div className="md:hidden absolute top-10 bottom-10 left-[1.75rem] w-0.5 border-l-2 border-dashed border-slate-200" data-id="element-21438" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6" data-id="element-21439">
              {[{
              icon: <User className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-21440" />,
              num: 1,
              title: 'Введите данные рождения',
              desc: 'Укажите дату, время и место рождения для точного расчёта.'
            }, {
              icon: <Eye className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-21441" />,
              num: 2,
              title: 'Получите бесплатный Beta-результат',
              desc: 'Мгновенный краткий анализ основных показателей.'
            }, {
              icon: <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-21442" />,
              num: 3,
              title: 'Разблокируйте полный Premium-отчёт',
              desc: 'Подробный персональный разбор с рекомендациями.'
            }].map(step => <div key={step.num} className="relative flex md:flex-col items-start md:items-center" data-id="element-21443">
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-white rounded-2xl md:rounded-full flex items-center justify-center mb-0 md:mb-4 border border-slate-100 shadow-sm relative z-10 flex-shrink-0 mr-4 md:mr-0" data-id="element-21444">
                    <div className="absolute -top-1.5 -right-1.5 md:top-0 md:right-0 w-5 h-5 md:w-6 md:h-6 bg-indigo-600 text-white text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center md:translate-x-1/4 md:-translate-y-1/4 shadow-sm" data-id="element-21445">
                      {step.num}
                    </div>
                    {step.icon}
                  </div>
                  <div className="md:text-center pt-1 md:pt-0" data-id="element-21446">
                    <h3 className="text-sm md:text-base font-bold text-slate-900 mb-1" data-id="element-21447">
                      {step.title}
                    </h3>
                    <p className="text-xs md:text-sm text-slate-500" data-id="element-21448">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. РАЗОВЫЕ УСЛУГИ */}
      <div ref={servicesRef} className="scroll-mt-4" data-id="element-21449">
        <AnimatedSection className="py-12 bg-slate-50" data-id="element-21450">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21451">
            <div className="text-center mb-10" data-id="element-21452">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-21453">
                Разовые астрологические услуги
              </h2>
              <p className="text-sm md:text-base text-slate-600 max-w-2xl mx-auto" data-id="element-21454">
                Выберите конкретный астрологический анализ и получите
                персональный отчёт.
              </p>
            </div>

            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-21455">
              {/* Натальная карта */}
              <motion.div variants={staggerItem} data-id="element-21456">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-indigo-200 transition-colors" data-id="element-21457">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-500" data-id="element-21458" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-21459">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center" data-id="element-21460">
                      <Moon className="w-5 h-5" data-id="element-21461" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21462">
                      Натальная карта
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21463">
                    Анализ личности, характера, талантов и жизненного
                    потенциала.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-21464">
                    <div className="text-xs text-slate-500" data-id="element-21465">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-21466">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21467">
                    <div className="flex items-center justify-between mb-2" data-id="element-21468">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21469">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-21470">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-21471">
                      {['Солнце', 'Луна', 'Асцендент', 'краткий психологический профиль'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-21472">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21473" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21474" />
                  <div className="mb-5 flex-grow" data-id="element-21475">
                    <div className="flex items-center justify-between mb-2" data-id="element-21476">
                      <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-21477">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-21478">
                        от 20 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-21479">
                      {['полный анализ натальной карты', 'планеты в знаках', 'планеты в домах', 'аспекты', 'таланты и предназначение', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21480">
                          <Check className="w-3.5 h-3.5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21481" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button fullWidth onClick={() => onNavigate('service-natal')} data-id="element-21482">
                    Построить карту
                  </Button>
                </Card>
              </motion.div>

              {/* Соляр */}
              <motion.div variants={staggerItem} data-id="element-21483">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-amber-200 transition-colors" data-id="element-21484">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" data-id="element-21485" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-21486">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center" data-id="element-21487">
                      <Sun className="w-5 h-5" data-id="element-21488" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21489">Соляр</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21490">
                    Астрологический прогноз на личный год.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-21491">
                    <div className="text-xs text-slate-500" data-id="element-21492">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-21493">
                      4 400 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21494">
                    <div className="flex items-center justify-between mb-2" data-id="element-21495">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21496">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-21497">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-21498">
                      {['главная тема года', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-21499">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21500" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21501" />
                  <div className="mb-5 flex-grow" data-id="element-21502">
                    <div className="flex items-center justify-between mb-2" data-id="element-21503">
                      <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-21504">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-21505">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-21506">
                      {['полный солярный анализ', 'ключевые сферы года', 'важные периоды', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21507">
                          <Check className="w-3.5 h-3.5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21508" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-solar')} data-id="element-21509">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Комбо */}
              <motion.div variants={staggerItem} data-id="element-21510">
                <Card padding="md" className="h-full flex flex-col border-violet-300 ring-2 ring-violet-100 shadow-lg shadow-violet-500/10 relative overflow-hidden group" data-id="element-21511">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500" data-id="element-21512" />
                  <div className="absolute top-3 right-3 z-10" data-id="element-21513">
                    <Badge variant="pro" className="bg-violet-100 text-violet-700 border border-violet-200 text-[10px]" data-id="element-21514">
                      Самый популярный
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mb-3 mt-1" data-id="element-21515">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-21516">
                      <Layers className="w-5 h-5" data-id="element-21517" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 pr-20" data-id="element-21518">
                      Комбо: Натал + Соляр
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21519">
                    Полный анализ личности и прогноз на ближайший год.
                  </p>
                  <div className="bg-violet-50 rounded-lg p-3 mb-4 border border-violet-100" data-id="element-21520">
                    <div className="text-xs text-violet-600 font-medium" data-id="element-21521">
                      Premium отчёт
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-21522">
                      <span className="text-2xl font-bold text-slate-900" data-id="element-21523">
                        6 900 ₽
                      </span>
                      <span className="text-sm text-slate-400 line-through" data-id="element-21524">
                        8 300 ₽
                      </span>
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21525">
                    <div className="flex items-center justify-between mb-2" data-id="element-21526">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21527">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-21528">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-21529">
                      {['краткий профиль личности', 'краткий прогноз года'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-21530">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21531" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21532" />
                  <div className="mb-5 flex-grow" data-id="element-21533">
                    <div className="flex items-center justify-between mb-2" data-id="element-21534">
                      <span className="text-xs font-bold text-violet-600 uppercase tracking-wider" data-id="element-21535">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded" data-id="element-21536">
                        от 35 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-21537">
                      {['полный анализ натальной карты', 'полный соляр', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21538">
                          <Check className="w-3.5 h-3.5 text-violet-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21539" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="primary" fullWidth onClick={() => onNavigate('service-combo')} className="bg-violet-600 hover:bg-violet-700" data-id="element-21540">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Совместимость */}
              <motion.div variants={staggerItem} data-id="element-21541">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-rose-200 transition-colors" data-id="element-21542">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-rose-400" data-id="element-21543" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-21544">
                    <div className="w-10 h-10 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center" data-id="element-21545">
                      <Heart className="w-5 h-5" data-id="element-21546" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21547">
                      Совместимость (Синастрия)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21548">
                    Анализ совместимости двух людей.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-21549">
                    <div className="text-xs text-slate-500" data-id="element-21550">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-21551">
                      4 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21552">
                    <div className="flex items-center justify-between mb-2" data-id="element-21553">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21554">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-21555">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-21556">
                      {['общий уровень совместимости', 'краткий анализ'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-21557">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21558" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21559" />
                  <div className="mb-5 flex-grow" data-id="element-21560">
                    <div className="flex items-center justify-between mb-2" data-id="element-21561">
                      <span className="text-xs font-bold text-rose-500 uppercase tracking-wider" data-id="element-21562">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-21563">
                        от 25 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-21564">
                      {['эмоциональная совместимость', 'романтическая динамика', 'конфликтные зоны', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21565">
                          <Check className="w-3.5 h-3.5 text-rose-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21566" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-compatibility')} data-id="element-21567">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Транзиты */}
              <motion.div variants={staggerItem} data-id="element-21568">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-teal-200 transition-colors" data-id="element-21569">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-teal-500" data-id="element-21570" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-21571">
                    <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center" data-id="element-21572">
                      <Activity className="w-5 h-5" data-id="element-21573" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21574">
                      Прогноз (Транзиты)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21575">
                    Астрологический прогноз на ближайшие месяцы.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-21576">
                    <div className="text-xs text-slate-500" data-id="element-21577">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-21578">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21579">
                    <div className="flex items-center justify-between mb-2" data-id="element-21580">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21581">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-21582">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-21583">
                      {['основной транзит', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-21584">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21585" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21586" />
                  <div className="mb-5 flex-grow" data-id="element-21587">
                    <div className="flex items-center justify-between mb-2" data-id="element-21588">
                      <span className="text-xs font-bold text-teal-600 uppercase tracking-wider" data-id="element-21589">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded" data-id="element-21590">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-21591">
                      {['полный анализ транзитов', 'ключевые даты', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21592">
                          <Check className="w-3.5 h-3.5 text-teal-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21593" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-forecast')} data-id="element-21594">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Консультация */}
              <motion.div variants={staggerItem} data-id="element-21595">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-sky-200 transition-colors" data-id="element-21596">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-sky-500" data-id="element-21597" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-21598">
                    <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center" data-id="element-21599">
                      <Video className="w-5 h-5" data-id="element-21600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21601">
                      Консультация с астрологом
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21602">
                    Персональная консультация с профессиональным астрологом.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-21603">
                    <div className="text-xs text-slate-500" data-id="element-21604">Личная встреча</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-21605">
                      от 2 990 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21606">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21607">
                      Beta:
                    </span>
                    <p className="text-xs text-slate-400 italic mt-1" data-id="element-21608">
                      Для консультаций Beta отсутствует
                    </p>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21609" />
                  <div className="mb-5 flex-grow" data-id="element-21610">
                    <span className="text-xs font-bold text-sky-600 uppercase tracking-wider" data-id="element-21611">
                      Формат:
                    </span>
                    <ul className="space-y-1 mt-2" data-id="element-21612">
                      {['онлайн-встреча по видеосвязи', 'персональный разбор карты', 'ответы на ваши вопросы'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21613">
                          <Check className="w-3.5 h-3.5 text-sky-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21614" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-consultation')} data-id="element-21615">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>

      {/* 4. ПАКЕТЫ ОТЧЁТОВ */}
      <AnimatedSection className="py-14 bg-white" data-id="element-21616">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21617">
          <div className="text-center mb-8" data-id="element-21618">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-21619">
              Пакеты отчётов
            </h2>
            <p className="text-sm md:text-base text-slate-600 max-w-2xl mx-auto" data-id="element-21620">
              Приобретите пакет астрологических анализов со скидкой и
              используйте их для разных услуг платформы.
            </p>
          </div>

          {/* Info bar */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8 bg-slate-50 rounded-lg px-4 py-3 border border-slate-100 max-w-4xl mx-auto" data-id="element-21621">
            <span className="text-xs sm:text-sm font-medium text-slate-700" data-id="element-21622">
              Каждый отчёт из пакета можно использовать для:
            </span>
            <div className="flex flex-wrap gap-1.5" data-id="element-21623">
              <span className="text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-md px-2 py-0.5" data-id="element-21624">
                Натальная карта
              </span>
              <span className="text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-md px-2 py-0.5" data-id="element-21625">
                Соляр
              </span>
              <span className="text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-md px-2 py-0.5" data-id="element-21626">
                Совместимость
              </span>
              <span className="text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-md px-2 py-0.5" data-id="element-21627">
                Прогноз (Транзиты)
              </span>
            </div>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5 mb-8" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-21628">
            {/* Пакет 20 отчётов */}
            <motion.div variants={staggerItem} data-id="element-21629">
              <div className="h-full flex flex-col bg-white rounded-xl border border-slate-200 p-5 hover:border-indigo-200 transition-colors" data-id="element-21630">
                <div className="flex items-center justify-between mb-3" data-id="element-21631">
                  <div className="flex items-center gap-3" data-id="element-21632">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center" data-id="element-21633">
                      <Package className="w-5 h-5" data-id="element-21634" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21635">
                      Пакет 20 отчётов
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-4" data-id="element-21636">
                  Идеально для первого знакомства с астрологией.
                </p>
                <div className="mb-4" data-id="element-21637">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-21638">
                    Вы сможете:
                  </p>
                  <ul className="space-y-1.5" data-id="element-21639">
                    {['построить свою натальную карту', 'получить прогноз на год', 'проверить совместимость с партнёром'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700" data-id="element-21640">
                        <Check className="w-3.5 h-3.5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21641" />
                        {t}
                      </li>)}
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 mb-5 border border-slate-100" data-id="element-21642">
                  <div className="flex items-baseline gap-2" data-id="element-21643">
                    <span className="text-2xl font-bold text-slate-900" data-id="element-21644">
                      19 900 ₽
                    </span>
                  </div>
                  <p className="text-xs text-indigo-600 font-medium mt-0.5" data-id="element-21645">
                    ≈ 995 ₽ за отчёт
                  </p>
                </div>
                <div className="mt-auto" data-id="element-21646">
                  <Button variant="outline" fullWidth onClick={() => handleBuyPackage({
                  title: 'Пакет 20 отчётов',
                  desc: 'Идеально для первого знакомства с астрологией.',
                  price: 19900
                })} data-id="element-21647">
                    Купить пакет
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Пакет 50 отчётов */}
            <motion.div variants={staggerItem} data-id="element-21648">
              <div className="h-full flex flex-col bg-white rounded-xl border-2 border-violet-300 ring-1 ring-violet-100 shadow-lg shadow-violet-500/10 p-5 relative overflow-hidden" data-id="element-21649">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-fuchsia-500" data-id="element-21650" />
                <div className="flex items-center justify-between mb-3" data-id="element-21651">
                  <div className="flex items-center gap-3" data-id="element-21652">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-21653">
                      <Package className="w-5 h-5" data-id="element-21654" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21655">
                      Пакет 50 отчётов
                    </h3>
                  </div>
                  <span className="text-[11px] font-semibold text-white bg-violet-600 rounded-md px-2 py-0.5" data-id="element-21656">
                    Самый популярный
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-4" data-id="element-21657">
                  Оптимальный выбор для анализа себя и близких.
                </p>
                <div className="mb-4" data-id="element-21658">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-21659">
                    Подходит если вы хотите:
                  </p>
                  <ul className="space-y-1.5" data-id="element-21660">
                    {['глубже изучить свою натальную карту', 'сделать анализ для партнёра', 'посмотреть прогноз на разные периоды', 'разобрать отношения'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700" data-id="element-21661">
                        <Check className="w-3.5 h-3.5 text-violet-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21662" />
                        {t}
                      </li>)}
                  </ul>
                </div>
                <div className="bg-violet-50 rounded-lg p-3 mb-5 border border-violet-100" data-id="element-21663">
                  <div className="flex items-baseline gap-2" data-id="element-21664">
                    <span className="text-2xl font-bold text-slate-900" data-id="element-21665">
                      44 900 ₽
                    </span>
                  </div>
                  <p className="text-xs text-violet-600 font-medium mt-0.5" data-id="element-21666">
                    ≈ 898 ₽ за отчёт
                  </p>
                </div>
                <div className="mt-auto" data-id="element-21667">
                  <Button variant="primary" fullWidth onClick={() => handleBuyPackage({
                  title: 'Пакет 50 отчётов',
                  desc: 'Оптимальный выбор для анализа себя и близких.',
                  price: 44900
                })} className="bg-violet-600 hover:bg-violet-700" data-id="element-21668">
                    Купить пакет
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Пакет 100 отчётов */}
            <motion.div variants={staggerItem} data-id="element-21669">
              <div className="h-full flex flex-col bg-white rounded-xl border border-emerald-200 hover:border-emerald-300 transition-colors shadow-sm p-5 relative overflow-hidden" data-id="element-21670">
                <div className="flex items-center justify-between mb-3" data-id="element-21671">
                  <div className="flex items-center gap-3" data-id="element-21672">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center" data-id="element-21673">
                      <Package className="w-5 h-5" data-id="element-21674" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21675">
                      Пакет 100 отчётов
                    </h3>
                  </div>
                  <span className="text-[11px] font-semibold text-white bg-emerald-600 rounded-md px-2 py-0.5" data-id="element-21676">
                    Максимальная выгода
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-4" data-id="element-21677">
                  Лучший вариант для глубокого изучения астрологии.
                </p>
                <div className="mb-4" data-id="element-21678">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-21679">
                    Подходит для:
                  </p>
                  <ul className="space-y-1.5" data-id="element-21680">
                    {['анализа нескольких людей', 'подарков друзьям и близким', 'регулярных прогнозов', 'долгосрочного использования сервиса'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700" data-id="element-21681">
                        <Check className="w-3.5 h-3.5 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21682" />
                        {t}
                      </li>)}
                  </ul>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 mb-5 border border-emerald-100" data-id="element-21683">
                  <div className="flex items-baseline gap-2" data-id="element-21684">
                    <span className="text-2xl font-bold text-slate-900" data-id="element-21685">
                      79 900 ₽
                    </span>
                  </div>
                  <p className="text-xs text-emerald-600 font-medium mt-0.5" data-id="element-21686">
                    ≈ 799 ₽ за отчёт
                  </p>
                </div>
                <div className="mt-auto" data-id="element-21687">
                  <Button variant="outline" fullWidth onClick={() => handleBuyPackage({
                  title: 'Пакет 100 отчётов',
                  desc: 'Лучший вариант для глубокого изучения астрологии.',
                  price: 79900
                })} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-id="element-21688">
                    Купить пакет
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Пояснение под пакетами */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-0 max-w-4xl mx-auto bg-slate-50 rounded-xl border border-slate-100 overflow-hidden" data-id="element-21689">
            <div className="flex items-center gap-3 px-5 py-3 sm:flex-1" data-id="element-21690">
              <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-21691">
                <FileText className="w-4 h-4" data-id="element-21692" />
              </div>
              <div data-id="element-21693">
                <span className="text-sm font-semibold text-slate-900" data-id="element-21694">
                  1 отчёт = 1 Premium анализ
                </span>
                <p className="text-[11px] text-slate-500 leading-tight" data-id="element-21695">
                  Используйте для любой услуги платформы
                </p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-10 bg-slate-200 flex-shrink-0" data-id="element-21696" />
            <div className="px-5 py-3 sm:flex-1" data-id="element-21697">
              <p className="text-[11px] text-slate-500 mb-1.5" data-id="element-21698">
                <span className="font-medium text-slate-700" data-id="element-21699">
                  Пример (пакет из 20):
                </span>
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-21700">
                <span className="text-[11px] font-medium text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-md px-2 py-0.5" data-id="element-21701">
                  Натальная карта
                </span>
                <span className="text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-100 rounded-md px-2 py-0.5" data-id="element-21702">
                  Соляр
                </span>
                <span className="text-[11px] font-medium text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-2 py-0.5" data-id="element-21703">
                  Совместимость
                </span>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 5. TRUST / GUARANTEE */}
      <section className="py-8 bg-white border-y border-slate-100" data-id="element-21704">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21705">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-21706">
            {[{
            icon: <Clock className="w-5 h-5 text-indigo-600" data-id="element-21707" />,
            bg: 'bg-indigo-50',
            title: 'Мгновенный результат',
            desc: 'Отчёт готов сразу после оплаты'
          }, {
            icon: <Shield className="w-5 h-5 text-emerald-600" data-id="element-21708" />,
            bg: 'bg-emerald-50',
            title: 'Безопасная оплата',
            desc: 'Надежное шифрование данных'
          }, {
            icon: <FileText className="w-5 h-5 text-amber-600" data-id="element-21709" />,
            bg: 'bg-amber-50',
            title: 'PDF-отчёт навсегда',
            desc: 'Доступен в личном кабинете'
          }, {
            icon: <MessageCircle className="w-5 h-5 text-sky-600" data-id="element-21710" />,
            bg: 'bg-sky-50',
            title: 'Поддержка 24/7',
            desc: 'Поможем с любым вопросом'
          }].map((item, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-21711">
                <div className={`w-10 h-10 rounded-full ${item.bg} flex items-center justify-center mb-2`} data-id="element-21712">
                  {item.icon}
                </div>
                <h4 className="text-xs font-bold text-slate-900 mb-0.5" data-id="element-21713">
                  {item.title}
                </h4>
                <p className="text-[10px] text-slate-500 leading-tight" data-id="element-21714">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. НЕ ЗНАЕТЕ, ЧТО ВЫБРАТЬ? */}
      <AnimatedSection className="py-12 bg-slate-50" data-id="element-21715">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21716">
          <div className="text-center mb-8" data-id="element-21717">
            <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mx-auto mb-3" data-id="element-21718">
              <HelpCircle className="w-5 h-5" data-id="element-21719" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-21720">
              Не знаете, что выбрать?
            </h2>
            <p className="text-sm text-slate-600" data-id="element-21721">
              Выберите вашу главную цель, и мы подскажем подходящую услугу
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-21722">
            {[{
            goal: 'Хочу понять себя',
            service: 'Натальная карта',
            page: 'service-natal',
            icon: <Moon className="w-4 h-4" data-id="element-21723" />,
            color: 'text-indigo-600',
            borderHover: 'hover:border-indigo-300'
          }, {
            goal: 'Хочу прогноз на год',
            service: 'Соляр',
            page: 'service-solar',
            icon: <Sun className="w-4 h-4" data-id="element-21724" />,
            color: 'text-amber-600',
            borderHover: 'hover:border-amber-300'
          }, {
            goal: 'Хочу личность и прогноз',
            service: 'Комбо: Натал + Соляр',
            page: 'service-combo',
            icon: <Layers className="w-4 h-4" data-id="element-21725" />,
            color: 'text-violet-600',
            borderHover: 'hover:border-violet-300'
          }, {
            goal: 'Хочу понять отношения',
            service: 'Совместимость',
            page: 'service-compatibility',
            icon: <Heart className="w-4 h-4" data-id="element-21726" />,
            color: 'text-rose-600',
            borderHover: 'hover:border-rose-300'
          }, {
            goal: 'Хочу прогноз на ближайшее время',
            service: 'Транзиты',
            page: 'service-forecast',
            icon: <Activity className="w-4 h-4" data-id="element-21727" />,
            color: 'text-teal-600',
            borderHover: 'hover:border-teal-300'
          }, {
            goal: 'Хочу личный разбор',
            service: 'Консультация',
            page: 'service-consultation',
            icon: <Video className="w-4 h-4" data-id="element-21728" />,
            color: 'text-sky-600',
            borderHover: 'hover:border-sky-300'
          }].map((item, i) => <button key={i} onClick={() => onNavigate(item.page)} className={`flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-200 text-left transition-all group hover:shadow-sm ${item.borderHover}`} data-id="element-21729">
                <div className={`w-9 h-9 rounded-lg bg-slate-50 ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-21730">
                  {item.icon}
                </div>
                <div className="flex-grow min-w-0" data-id="element-21731">
                  <div className="text-sm font-bold text-slate-900" data-id="element-21732">
                    {item.goal}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-21733">→ {item.service}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 group-hover:text-slate-600 transition-colors" data-id="element-21734" />
              </button>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 7. FAQ */}
      <AnimatedSection className="py-14 bg-white" data-id="element-21735">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21736">
          <div className="text-center mb-8" data-id="element-21737">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-21738">
              Часто задаваемые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Что такое Beta-результат?',
          content: 'Beta — это бесплатный предварительный анализ, который вы получаете сразу после ввода данных рождения. Он показывает основные астрологические показатели и позволяет познакомиться с форматом анализа перед покупкой полного отчёта.'
        }, {
          id: '2',
          title: 'Чем Premium отличается от Beta?',
          content: 'Premium — это полный подробный отчёт с разбором всех элементов карты, прогнозами и практическими рекомендациями. Объём Premium-отчёта значительно больше и включает глубокий персональный анализ.'
        }, {
          id: '3',
          title: 'Можно ли использовать пакет для разных услуг?',
          content: 'Да! Каждый отчёт из пакета можно использовать для любой Premium-услуги: натальная карта, соляр, совместимость или прогноз (транзиты). Вы сами выбираете, на что потратить каждый отчёт.'
        }, {
          id: '4',
          title: 'В каком формате приходит отчёт?',
          content: 'Все отчёты предоставляются в формате PDF. Вы можете скачать их на странице результата сразу после оплаты, а также получить на email. Отчёты доступны в вашем личном кабинете бессрочно.'
        }, {
          id: '5',
          title: 'Сколько времени занимает расчёт?',
          content: 'Автоматические отчёты генерируются мгновенно — сразу после оплаты. Консультация с астрологом назначается на удобное для вас время.'
        }]} data-id="element-21739" />
        </div>
      </AnimatedSection>

      {/* 8. ФИНАЛЬНЫЙ CTA */}
      <AnimatedSection className="py-16 bg-slate-900 relative overflow-hidden" data-id="element-21740">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f46e520,#8b5cf620)]" data-id="element-21741" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-21742">
          <motion.div animate={{
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="inline-block mb-5" data-id="element-21743">
            <Star className="w-8 h-8 text-indigo-400" data-id="element-21744" />
          </motion.div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4" data-id="element-21745">
            Начните исследовать свою натальную карту
          </h2>
          <p className="text-base text-indigo-100/80 mb-8 max-w-xl mx-auto" data-id="element-21746">
            Введите данные рождения и получите персональный астрологический
            анализ.
          </p>
          <Button size="lg" variant="outline" onClick={() => onNavigate('free-chart')} className="shadow-xl shadow-indigo-500/25 !bg-white !text-indigo-600 !border-white/30 hover:!bg-indigo-50" data-id="element-21747">
            Построить карту
            <ArrowRight className="ml-2 w-5 h-5" data-id="element-21748" />
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-indigo-200/60" data-id="element-21749">
            <span className="flex items-center" data-id="element-21750">
              <Shield className="w-4 h-4 mr-1.5 text-emerald-400/80" data-id="element-21751" />{' '}
              Безопасная оплата
            </span>
            <span className="flex items-center" data-id="element-21752">
              <Clock className="w-4 h-4 mr-1.5 text-indigo-300/80" data-id="element-21753" /> Мгновенный
              результат
            </span>
          </div>
        </div>
      </AnimatedSection>
    </div>;
}